import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const EXCLUDED_PATHS = new Set([
  "/login",
  "/signup",
  "/forgot-password",
  "/forgotpassword",
  "/403",
  "/",
]);

/**
 * PathTracker
 * Tracks the current route (pathname + search) per tab in sessionStorage.
 * Excludes unprotected routes like /login, /signup, /forgot-password.
 * Only tracks paths when user is authenticated to prevent setting paths during logout.
 * Works alongside NavigationHistoryContext for backward compatibility.
 * Renders nothing.
 */
const PathTracker: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const { pathname, search } = location;
    // Only track paths when user is authenticated and path is not excluded
    if (isAuthenticated && !EXCLUDED_PATHS.has(pathname)) {
      const value = `${pathname}${search || ""}`;
      sessionStorage.setItem("lastVisitedPath", value);
      // Clear intendedDestination now that the user has successfully
      // navigated to a protected route after login. This is done here
      // (rather than in the root-route IIFE) to avoid a race condition
      // where isPermissionsLoading re-renders clear it prematurely.
      sessionStorage.removeItem("intendedDestination");
    } else {
      sessionStorage.removeItem("lastVisitedPath");
    }
  }, [location, isAuthenticated]);

  return null;
};

export default PathTracker;
